/**
 * Snapshot capture pipeline (Phase 1B-A).
 *
 * `captureFrameAsBlob` grabs the current frame from a <video> element,
 * encodes it (PNG default, JPEG via the `mime` arg), and returns the bytes
 * + SHA-256 digest. Hash is computed client-side so it travels with the
 * upload-init request — server stores it in `Evidence.sha256` and an async
 * Celery task re-hashes the uploaded object to flip
 * `integrity_jsonb.sha256_verified` from `false` → `true`.
 *
 * `uploadSnapshot` runs the two-phase flow:
 *   1) POST /sessions/{id}/evidence/init → reserves an Evidence row + presigned URL
 *   2) PUT bytes → presigned URL (or to the mock if backend has no MinIO)
 *   3) POST /evidence/{id}/complete → backend HEADs the object to verify it exists
 *
 * For Phase 1B-A the operator captures from the field's incoming track,
 * so the GPS attached to the row is `session.geo_jsonb` flagged
 * `source: "session_baseline"`. Phase 1B-B routes capture through the
 * field user so the GPS reflects capture-time location.
 */
import axios from "axios";

import { ApiPaths, api } from "./api";
import type { Evidence, EvidenceInitResponse } from "./types";

export type CapturedFrame = {
  blob: Blob;
  sha256: string;
  width: number;
  height: number;
  mime: string;
};

export type SnapshotGeo = {
  lat?: number;
  lng?: number;
  accuracy_m?: number;
  source?: string;
  timestamp?: number;
};

/**
 * Capture a single frame from a live <video> element, encode it, and hash.
 *
 * Throws if the video element has no intrinsic dimensions yet (track not
 * playing). The callable assumes the video is from the same origin (or the
 * tracks are coming from LiveKit's MediaStream, which doesn't taint the
 * canvas).
 *
 * Defaults to JPEG, not PNG (backlog 2026-09-18). PNG is lossless — a
 * 1280×720+ inspection photo easily ran several MB, and `quality` is
 * ignored for PNG entirely, so there was no way to trade fidelity for
 * size. On a weak field connection that multi-MB PUT to the presigned
 * URL is what made "capture" feel like it hung. JPEG at 0.85 is
 * visually indistinguishable for a compliance photo and typically
 * 5-10x smaller.
 */
export async function captureFrameAsBlob(
  video: HTMLVideoElement,
  mime: "image/png" | "image/jpeg" = "image/jpeg",
  quality = 0.85,
): Promise<CapturedFrame> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) {
    throw new Error("video has no frame yet — wait for the track to start playing");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("could not allocate 2D canvas context");
  ctx.drawImage(video, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mime, quality),
  );
  if (!blob) throw new Error("canvas.toBlob returned null");

  const sha256 = await sha256Hex(await blob.arrayBuffer());
  return { blob, sha256, width, height, mime };
}

/** Hex-digest of a SHA-256 hash; uses Web Crypto. */
export async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export type SnapshotInput = {
  sessionId: string;
  frame: CapturedFrame;
  capturedAt?: Date;
  geo?: SnapshotGeo;
  device?: Record<string, unknown>;
};

/**
 * Run the full upload pipeline. Returns the completed Evidence row.
 */
export async function uploadSnapshot(input: SnapshotInput): Promise<Evidence> {
  const captured_at = (input.capturedAt ?? new Date()).toISOString();

  const init = await api.post<EvidenceInitResponse>(
    ApiPaths.sessionEvidenceInit(input.sessionId),
    {
      kind: "snapshot",
      mime: input.frame.mime,
      byte_size: input.frame.blob.size,
      sha256: input.frame.sha256,
      captured_at,
      geo: input.geo ?? {},
      device: input.device ?? defaultDeviceInfo(input.frame),
    },
  );

  const { upload_url, headers } = init.data;

  // The mock-s3:// scheme means the backend has no live MinIO; skip the PUT.
  if (!upload_url.startsWith("mock-s3://")) {
    // axios.put against an absolute URL bypasses our /api interceptor's
    // Bearer header, which is correct — presigned URLs sign their own auth.
    await axios.put(upload_url, input.frame.blob, { headers });
  }

  const completed = await api.post<Evidence>(
    ApiPaths.evidenceComplete(init.data.evidence.id),
    {},
  );
  return completed.data;
}

/**
 * Upload the audio of an interview conducted OUTSIDE the platform.
 *
 * Reported 2026-09-25: "Some controls cannot be completed due to
 * connection issues. In these cases, I conduct the interview by phone
 * using Quicktalk. I would need the option to upload the audio
 * recording directly to the client's profile."
 *
 * Same two-phase init → PUT → complete pipeline as a snapshot, so the
 * file is hashed client-side and the hash is what the server verifies.
 * The kind is `external_audio`, never `recording`: the platform can
 * vouch for an egress it produced and cannot vouch for what happened
 * to a phone recording before it arrived, and the report says so.
 */
/**
 * How far along an external-audio upload is.
 *
 * Three phases, because a phone interview can be 200 MB and the bytes are
 * only one of them: hashing a file that size is not instant, and the
 * complete call afterwards is what actually chains the evidence. A bar
 * that sat at 100% through "finalising" would be lying about being done,
 * so the phase is reported alongside the fraction.
 *
 * `loaded`/`total` are only meaningful while uploading.
 */
export type UploadPhase = "hashing" | "uploading" | "finalising";

export interface UploadProgress {
  phase: UploadPhase;
  /** 0..1 for the current phase, or null when it cannot be measured. */
  fraction: number | null;
  loaded?: number;
  total?: number;
}

/** Extension → audio MIME, for files the browser could not type. */
const AUDIO_MIME_BY_EXT: ReadonlyArray<[string, string]> = [
  [".mp3", "audio/mpeg"],
  [".m4a", "audio/mp4"],
  [".mp4", "audio/mp4"],
  [".aac", "audio/aac"],
  [".wav", "audio/wav"],
  [".ogg", "audio/ogg"],
  [".opus", "audio/ogg"],
  [".amr", "audio/amr"],
  [".3gp", "audio/3gpp"],
  [".caf", "audio/x-caf"],
  [".wma", "audio/x-ms-wma"],
  [".flac", "audio/flac"],
  [".aiff", "audio/aiff"],
];

/**
 * The `audio/*` MIME to declare for an uploaded phone recording.
 *
 * The picker deliberately accepts what phones actually produce: a voice
 * memo typed `video/mp4` / `video/3gpp` (an audio-only mp4 container), or
 * an .amr / .m4a with an EMPTY `type`. But the server only files
 * external_audio under an `audio/*` MIME — sending the browser's
 * `video/mp4` through got a 400 and "Could not upload the audio", after
 * the picker had said yes. Found 2026-10-08 while tracing a user's
 * missing phone audio: no row, pending or otherwise, ever existed.
 *
 * Only the label changes; the bytes are the file as recorded.
 */
export function audioMimeFor(file: File): string {
  if (file.type.startsWith("audio/")) return file.type;
  if (file.type === "video/mp4") return "audio/mp4";
  if (file.type === "video/3gpp") return "audio/3gpp";
  const lower = file.name.toLowerCase();
  const hit = AUDIO_MIME_BY_EXT.find(([ext]) => lower.endsWith(ext));
  return hit ? hit[1] : "audio/mpeg";
}

export async function uploadExternalAudio(input: {
  sessionId: string;
  file: File;
  /** When the interview actually happened — not when it was uploaded.
   *  Defaults to the file's own mtime, which is the closest thing to
   *  the truth we have without asking. */
  capturedAt?: Date;
  note?: string;
  onProgress?: (p: UploadProgress) => void;
}): Promise<Evidence> {
  const report = input.onProgress ?? (() => {});
  // Hashing is not measurable — crypto.subtle.digest is one call over the
  // whole buffer — so this phase is announced, not quantified.
  report({ phase: "hashing", fraction: null });
  const buf = await input.file.arrayBuffer();
  const sha256 = await sha256Hex(buf);
  const captured_at = (
    input.capturedAt ?? new Date(input.file.lastModified || Date.now())
  ).toISOString();

  const init = await api.post<EvidenceInitResponse>(
    ApiPaths.sessionEvidenceInit(input.sessionId),
    {
      kind: "external_audio",
      mime: audioMimeFor(input.file),
      byte_size: input.file.size,
      sha256,
      captured_at,
      geo: {},
      device: {
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        captured_via: "external-upload",
        original_filename: input.file.name,
        note: input.note ?? "",
      },
    },
  );

  const { upload_url, headers } = init.data;
  report({ phase: "uploading", fraction: 0, loaded: 0, total: input.file.size });
  if (!upload_url.startsWith("mock-s3://")) {
    await axios.put(upload_url, input.file, {
      headers,
      onUploadProgress: (e) => {
        // e.total is absent on some browsers for streamed bodies; fall
        // back to the size we already know rather than dropping to
        // indeterminate mid-upload.
        const total = e.total ?? input.file.size;
        report({
          phase: "uploading",
          fraction: total > 0 ? Math.min(1, e.loaded / total) : null,
          loaded: e.loaded,
          total,
        });
      },
    });
  }
  report({ phase: "uploading", fraction: 1, loaded: input.file.size, total: input.file.size });

  report({ phase: "finalising", fraction: null });
  const completed = await api.post<Evidence>(
    ApiPaths.evidenceComplete(init.data.evidence.id),
    {},
  );
  return completed.data;
}

function defaultDeviceInfo(frame: CapturedFrame): Record<string, unknown> {
  return {
    ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
    width: frame.width,
    height: frame.height,
    captured_via: "operator-side", // Phase 1B-B field path uses uploadFieldSnapshot
  };
}


/**
 * Field-routed snapshot upload (Phase 1B-B).
 *
 * Same three-step pipeline, but authorised by the field-session token
 * instead of a JWT. Hand-rolled axios calls so we can attach the
 * `X-Field-Session-Token` header without contaminating the shared `api`
 * instance (its request interceptor would attach a Bearer otherwise).
 */
export async function uploadFieldSnapshot(input: {
  sessionId: string;
  frame: CapturedFrame;
  fieldSessionToken: string;
  capturedAt?: Date;
  geo?: SnapshotGeo;
  device?: Record<string, unknown>;
}): Promise<Evidence> {
  const captured_at = (input.capturedAt ?? new Date()).toISOString();
  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const fieldHeader = { "X-Field-Session-Token": input.fieldSessionToken };

  const initRes = await axios.post<EvidenceInitResponse>(
    `${apiBase}/v1/sessions/${input.sessionId}/evidence/init`,
    {
      kind: "snapshot",
      mime: input.frame.mime,
      byte_size: input.frame.blob.size,
      sha256: input.frame.sha256,
      captured_at,
      geo: input.geo ?? {},
      device: input.device ?? {
        ...defaultDeviceInfo(input.frame),
        captured_via: "field-side",
      },
    },
    { headers: { "Content-Type": "application/json", ...fieldHeader } },
  );

  const { upload_url, headers } = initRes.data;
  if (!upload_url.startsWith("mock-s3://")) {
    await axios.put(upload_url, input.frame.blob, { headers });
  }

  const completedRes = await axios.post<Evidence>(
    `${apiBase}/v1/evidence/${initRes.data.evidence.id}/complete`,
    {},
    { headers: { "Content-Type": "application/json", ...fieldHeader } },
  );
  return completedRes.data;
}